import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  DoorOpen,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X as XIcon,
  Clock,
  Users,
  Stethoscope,
  AlertTriangle,
  CalendarX,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation } from "@/contexts/LocationContext";
import { useGlobalToast } from "../contexts/ToastContext";
import { usePermissions } from "../hooks/usePermissions";

const COLORS = [
  { value: "#6366f1", label: "Índigo" },
  { value: "#10b981", label: "Esmeralda" },
  { value: "#f59e0b", label: "Ámbar" },
  { value: "#ef4444", label: "Rojo" },
  { value: "#3b82f6", label: "Azul" },
  { value: "#8b5cf6", label: "Violeta" },
  { value: "#ec4899", label: "Rosa" },
  { value: "#14b8a6", label: "Teal" },
];

interface ServiceOption {
  id: string;
  name: string;
  color: string;
}

interface SpecialistOption {
  id: string;
  firstName: string;
  lastName: string | null;
  color: string;
}

interface RoomService {
  service: ServiceOption;
}

interface RoomSpecialist {
  specialist: SpecialistOption;
}

interface AppointmentRoom {
  id: string;
  name: string;
  description: string | null;
  color: string;
  isActive: boolean;
  sortOrder: number;
  services: RoomService[];
  specialists: RoomSpecialist[];
}

const emptyForm = {
  name: "",
  description: "",
  color: "#6366f1",
  isActive: true,
  serviceIds: [] as string[],
  specialistIds: [] as string[],
};

export default function AppointmentRooms() {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { can } = usePermissions();
  const canManage = can("servicios:editar");
  const { currentLocationId } = useLocation();

  const [rooms, setRooms] = useState<AppointmentRoom[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [specialists, setSpecialists] = useState<SpecialistOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{
    message: string;
    isBookingModeMismatch: boolean;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<AppointmentRoom | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const locationId = currentLocationId;

  const fetchAll = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = locationId
        ? `?locationId=${locationId}&includeInactive=true`
        : "?includeInactive=true";
      const [roomsRes, servicesRes, specialistsRes] = await Promise.allSettled([
        apiClient.get(`/appointment-rooms${params}`),
        apiClient.get("/services"),
        apiClient.get(
          `/specialists${locationId ? `?locationId=${locationId}` : ""}`,
        ),
      ]);

      if (roomsRes.status === "rejected") {
        const response = (
          roomsRes.reason as { response?: { status?: number; data?: unknown } }
        )?.response;
        const status = response?.status;
        const data = response?.data as
          | { code?: string; message?: string }
          | undefined;
        const message = data?.message ?? "";
        const isBookingModeMismatch =
          status === 403 &&
          (data?.code === "BOOKING_MODE_MISMATCH" ||
            message.includes("BOOKING_MODE_MISMATCH"));
        setLoadError({
          message: isBookingModeMismatch
            ? "Esta sección es solo para negocios que trabajan con citas."
            : message || "No se pudieron cargar las salas",
          isBookingModeMismatch,
        });
      } else {
        setRooms(roomsRes.value.data.data || []);
      }
      setServices(
        servicesRes.status === "fulfilled"
          ? servicesRes.value.data.data || []
          : [],
      );
      setSpecialists(
        specialistsRes.status === "fulfilled"
          ? specialistsRes.value.data.data || []
          : [],
      );
    } catch {
      setLoadError({
        message: "No se pudieron cargar las salas",
        isBookingModeMismatch: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const openCreate = () => {
    setEditingRoom(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (room: AppointmentRoom) => {
    setEditingRoom(room);
    setForm({
      name: room.name,
      description: room.description || "",
      color: room.color,
      isActive: room.isActive,
      serviceIds: room.services.map((rs) => rs.service.id),
      specialistIds: room.specialists.map((rs) => rs.specialist.id),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRoom(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      let roomId: string;
      if (editingRoom) {
        await apiClient.put(`/appointment-rooms/${editingRoom.id}`, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          color: form.color,
          isActive: form.isActive,
        });
        roomId = editingRoom.id;
      } else {
        const res = await apiClient.post("/appointment-rooms", {
          name: form.name.trim(),
          description: form.description.trim() || null,
          color: form.color,
          isActive: form.isActive,
          locationId: locationId || undefined,
        });
        roomId = res.data.data.id;
      }
      // Actualizar servicios y especialistas (no crítico — la sala ya se creó)
      await Promise.allSettled([
        apiClient.put(`/appointment-rooms/${roomId}/services`, {
          serviceIds: form.serviceIds,
        }),
        apiClient.put(`/appointment-rooms/${roomId}/specialists`, {
          specialistIds: form.specialistIds,
        }),
      ]);
      showToast(editingRoom ? "Sala actualizada" : "Sala creada correctamente");
      closeModal();
      fetchAll();
    } catch (err: any) {
      const msg = err.response?.data?.message || "Error al guardar";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await apiClient.delete(`/appointment-rooms/${id}`);
      setDeleteConfirm(null);
      showToast("Sala eliminada");
      fetchAll();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al eliminar", "error");
    } finally {
      setDeleting(false);
    }
  };

  const toggleService = (id: string) => {
    setForm((f) => ({
      ...f,
      serviceIds: f.serviceIds.includes(id)
        ? f.serviceIds.filter((s) => s !== id)
        : [...f.serviceIds, id],
    }));
  };

  const toggleSpecialist = (id: string) => {
    setForm((f) => ({
      ...f,
      specialistIds: f.specialistIds.includes(id)
        ? f.specialistIds.filter((s) => s !== id)
        : [...f.specialistIds, id],
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DoorOpen className="w-6 h-6 text-indigo-600" />
            Salas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestiona las salas o cabinas del centro y sus servicios asociados
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!canManage}
          title={canManage ? undefined : "No tienes permiso para editar servicios"}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Nueva sala
        </button>
      </div>

      {/* Contenido */}
      {loadError ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center gap-4 text-center">
          {loadError.isBookingModeMismatch ? (
            <>
              <CalendarX className="w-10 h-10 text-gray-400" />
              <p className="text-gray-600 max-w-sm">{loadError.message}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                Volver al panel
              </Link>
            </>
          ) : (
            <>
              <AlertTriangle className="w-10 h-10 text-red-500" />
              <p className="text-red-600 max-w-sm">{loadError.message}</p>
              <button
                onClick={fetchAll}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            </>
          )}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : rooms.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <DoorOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-gray-700 font-medium mb-1">
            No hay salas configuradas
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Crea salas para controlar la disponibilidad por espacio físico
          </p>
          <button
            onClick={openCreate}
            disabled={!canManage}
            title={canManage ? undefined : "No tienes permiso para editar servicios"}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Crear primera sala
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rooms.map((room) => (
            <div
              key={room.id}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden ${!room.isActive ? "opacity-60" : ""}`}
            >
              {/* Franja de color */}
              <div className="h-1.5" style={{ backgroundColor: room.color }} />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: room.color }}
                    >
                      {room.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {room.name}
                      </h3>
                      {!room.isActive && (
                        <span className="text-xs text-gray-400">
                          (inactiva)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() =>
                        navigate(`/appointment-rooms/${room.id}/schedule`)
                      }
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Horario de la sala"
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEdit(room)}
                      disabled={!canManage}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        canManage
                          ? "Editar"
                          : "No tienes permiso para editar servicios"
                      }
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(room.id)}
                      disabled={!canManage}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        canManage
                          ? "Eliminar"
                          : "No tienes permiso para editar servicios"
                      }
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {room.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {room.description}
                  </p>
                )}

                {/* Servicios */}
                {room.services.length > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <Stethoscope className="w-3 h-3" />
                      <span>Servicios</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {room.services.map((rs) => (
                        <span
                          key={rs.service.id}
                          className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: rs.service.color }}
                        >
                          {rs.service.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profesionales */}
                {room.specialists.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <Users className="w-3 h-3" />
                      <span>Profesionales</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {room.specialists.map((rs) => (
                        <span
                          key={rs.specialist.id}
                          className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: rs.specialist.color }}
                        >
                          {rs.specialist.firstName}{" "}
                          {rs.specialist.lastName || ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {room.services.length === 0 &&
                  room.specialists.length === 0 && (
                    <p className="text-xs text-gray-400 italic">
                      Sin servicios ni profesionales asignados
                    </p>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingRoom ? "Editar sala" : "Nueva sala"}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Sala 1, Cabina Facial..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="Descripción opcional de la sala..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.value}
                      title={c.label}
                      onClick={() => setForm((f) => ({ ...f, color: c.value }))}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        form.color === c.value
                          ? "border-gray-800 scale-110"
                          : "border-transparent hover:scale-105"
                      }`}
                      style={{ backgroundColor: c.value }}
                    />
                  ))}
                </div>
              </div>

              {/* Activa */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setForm((f) => ({ ...f, isActive: !f.isActive }))
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.isActive ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.isActive ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700">Sala activa</span>
              </div>

              {/* Servicios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Servicios que se realizan en esta sala
                </label>
                {services.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay servicios disponibles
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {services.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.serviceIds.includes(s.id)}
                          onChange={() => toggleService(s.id)}
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: s.color }}
                        />
                        <span className="text-sm text-gray-700">{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Profesionales */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profesionales que trabajan en esta sala
                </label>
                {specialists.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay profesionales disponibles
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {specialists.map((sp) => (
                      <label
                        key={sp.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.specialistIds.includes(sp.id)}
                          onChange={() => toggleSpecialist(sp.id)}
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: sp.color }}
                        />
                        <span className="text-sm text-gray-700">
                          {sp.firstName} {sp.lastName || ""}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim() || !canManage}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingRoom ? "Guardar cambios" : "Crear sala"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-900 mb-2">
              ¿Eliminar sala?
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Esta acción no se puede deshacer. Las citas pasadas no se
              eliminarán.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting || !canManage}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
